import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  useDataProvider,
  useGetIdentity,
  useNotify,
  useTranslate,
} from "ra-core";

import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

import type { CrmDataProvider } from "../providers/types";

/**
 * Almikia Epic EN (TASK-091): the profile preference 'Recibir resumenes de las 8:30 y 20:30'.
 * It changes only the caller's OWN `summaries_enabled` (through `set_my_internal_summaries`) and
 * is shown only while the module is ON for her (direccion switches it on per user); new-lead and
 * urgent notices are not affected by it.
 */
export const MyInternalSummariesSwitch = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const queryClient = useQueryClient();
  const { identity } = useGetIdentity();
  const queryKey = ["internalNotices", "mine", identity?.id] as const;
  const prefs = useQuery({
    queryKey,
    queryFn: () => dataProvider.getMyInternalNoticePrefs(identity!.id),
    enabled: identity?.id != null,
    retry: false,
  });
  const save = useMutation({
    mutationFn: (value: boolean) => dataProvider.setMyInternalSummaries(value),
    onSuccess: () => {
      notify("crm.internalNotices.profile.saved");
      return queryClient.invalidateQueries({ queryKey });
    },
    onError: () =>
      notify("crm.internalNotices.profile.error", { type: "error" }),
  });

  if (!prefs.data?.moduleEnabled) return null;

  return (
    <Card data-testid="internal-summaries-card">
      <CardContent className="space-y-2">
        <div className="flex min-h-11 items-center gap-3">
          <Switch
            id="internal-summaries"
            checked={prefs.data.summariesEnabled}
            disabled={save.isPending}
            onCheckedChange={(value) => save.mutate(value)}
          />
          <Label htmlFor="internal-summaries" className="text-[15px]">
            {translate("crm.internalNotices.profile.summaries_label")}
          </Label>
        </div>
        <p className="text-[15px] text-muted-foreground">
          {translate("crm.internalNotices.profile.summaries_help")}
        </p>
      </CardContent>
    </Card>
  );
};
